import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AppBar, Box, Divider, Stack, Toolbar, useMediaQuery } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessIcon from "@mui/icons-material/Business";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import EmergencyIcon from "@mui/icons-material/Emergency";
import PeopleIcon from "@mui/icons-material/People";

import { UserType } from "../../types";
import { userHasPermission } from "../../types-helpers";

import { AccountDropdown } from "./account-dropdown";
import HeaderIconButton from "./header-icon-button";
import { ClearCallLogo, ClearCallTitle } from "../clear-call-logo";
import { RouteType } from "../../router";
import { useGetCurrentUser } from "../../store";
import { canManageUsersPage } from "../../utils/user-permissions";

interface IndicatorPosition {
  left: number;
  width: number;
}

// Each layout renders its own HeaderToolbar, so it remounts on navigation.
// Keeping the last position here lets the new instance start exactly where
// the bar already was, so nothing animates after a page change.
let lastIndicatorPosition: IndicatorPosition | null = null;

const HeaderToolbar: React.FC<PropsWithChildren> = ({ children }) => {
  const { data: currentUser } = useGetCurrentUser();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const showFullNavButtons = useMediaQuery("(min-width:1000px)");
  const [hoveredRoute, setHoveredRoute] = useState<RouteType | null>(null);
  const [indicatorStyle, setIndicatorStyle] =
    useState<IndicatorPosition | null>(lastIndicatorPosition);
  const navStackRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Partial<Record<RouteType, HTMLAnchorElement | null>>>({});
  const canViewClientSettings = userHasPermission(
    UserType.SysAdmin,
    currentUser,
  );
  const canViewUsersPage = canManageUsersPage(currentUser);

  const availableRoutes = useMemo(() => {
    const routes: RouteType[] = [];

    routes.push(RouteType.Calendar);

    if (canViewClientSettings) {
      routes.push(RouteType.Client);
    }

    routes.push(RouteType.Teams);

    if (canViewUsersPage) {
      routes.push(RouteType.Users);
    }

    routes.push(RouteType.Patients);

    return routes;
  }, [canViewClientSettings, canViewUsersPage]);

  const activeRoute = useMemo(() => {
    if (pathname === RouteType.Team || pathname.startsWith(`${RouteType.Team}/`)) {
      return RouteType.Teams;
    }

    if (pathname === RouteType.Patient || pathname.startsWith(`${RouteType.Patient}/`)) {
      return RouteType.Patients;
    }

    return availableRoutes.find(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  }, [availableRoutes, pathname]);

  const setButtonRef = useCallback(
    (route: RouteType) => (element: HTMLAnchorElement | null) => {
      buttonRefs.current[route] = element;
    },
    [],
  );

  const updateIndicatorPosition = useCallback(() => {
    if (!showFullNavButtons) {
      return;
    }

    const targetRoute = hoveredRoute ?? activeRoute;
    const navElement = navStackRef.current;

    if (!targetRoute) {
      lastIndicatorPosition = null;
      setIndicatorStyle(null);
      return;
    }

    if (!navElement) {
      return;
    }

    const targetElement = buttonRefs.current[targetRoute];

    if (!targetElement) {
      return;
    }

    const navRect = navElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const nextPosition = {
      left: targetRect.left - navRect.left,
      width: targetRect.width,
    };

    lastIndicatorPosition = nextPosition;
    setIndicatorStyle(nextPosition);
  }, [activeRoute, hoveredRoute, showFullNavButtons]);

  useLayoutEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition]);

  useEffect(() => {
    window.addEventListener("resize", updateIndicatorPosition);

    return () => {
      window.removeEventListener("resize", updateIndicatorPosition);
    };
  }, [updateIndicatorPosition]);

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        p: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        background:
          "linear-gradient(0deg,rgb(160, 225, 235),rgb(194, 226, 241))",
      }}
    >
      <Toolbar
        disableGutters
        variant="dense"
        sx={{
          minHeight: "52px !important",
          height: 52,
          maxHeight: 52,
        }}
      >
        <Stack
          direction="row"
          position="relative"
          justifyContent="space-between"
          alignItems="stretch"
          width={"100%"}
          height={"100%"}
          px={2}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={() => navigate(RouteType.Calendar)}
            sx={{
              cursor: "pointer",
            }}
          >
            <ClearCallLogo size={28} />
            <ClearCallTitle size={18} />
          </Stack>

          <Stack
            id="navigation-toolbar-stack"
            ref={navStackRef}
            direction="row"
            alignItems={showFullNavButtons ? "stretch" : "center"}
            justifyItems={"center"}
            height={"100%"}
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            {showFullNavButtons && indicatorStyle && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: indicatorStyle.left,
                  width: indicatorStyle.width,
                  height: "2px",
                  backgroundColor: "#272827",
                  transition:
                    "left 240ms cubic-bezier(0.2, 0.7, 0.1, 1), width 240ms cubic-bezier(0.2, 0.7, 0.1, 1)",
                  pointerEvents: "none",
                }}
              />
            )}
            <Stack direction="row" spacing={0} height={"100%"}>
              <HeaderIconButton
                tooltip="Calendar"
                showLabel={showFullNavButtons}
                buttonRef={setButtonRef(RouteType.Calendar)}
                onHoverStart={setHoveredRoute}
                onHoverEnd={() => setHoveredRoute(null)}
                icon={
                  <CalendarMonthIcon
                    sx={{
                      color: "#272827",
                    }}
                  />
                }
                route={RouteType.Calendar}
              />
              {/* <HeaderIconButton
                tooltip="3D Model Viewer"
                showLabel={showFullNavButtons}
                icon={
                  <ViewInArIcon
                    sx={{
                      color: "#272827",
                    }}
                  />
                }
                route={RouteType.ModelViewer}
              /> */}

              {canViewClientSettings && (
                <HeaderIconButton
                  tooltip="Client"
                  showLabel={showFullNavButtons}
                  buttonRef={setButtonRef(RouteType.Client)}
                  onHoverStart={setHoveredRoute}
                  onHoverEnd={() => setHoveredRoute(null)}
                  icon={
                    <BusinessIcon
                      sx={{
                        color: "#272827",
                      }}
                    />
                  }
                  route={RouteType.Client}
                />
              )}

              {canViewUsersPage && (
                <HeaderIconButton
                  tooltip={"Users"}
                  showLabel={showFullNavButtons}
                  buttonRef={setButtonRef(RouteType.Users)}
                  onHoverStart={setHoveredRoute}
                  onHoverEnd={() => setHoveredRoute(null)}
                  icon={
                    <PeopleIcon
                      sx={{
                        color: "#272827",
                      }}
                    />
                  }
                  route={RouteType.Users}
                />
              )}

              <HeaderIconButton
                tooltip="Teams"
                showLabel={showFullNavButtons}
                buttonRef={setButtonRef(RouteType.Teams)}
                onHoverStart={setHoveredRoute}
                onHoverEnd={() => setHoveredRoute(null)}
                icon={
                  <WorkspacesIcon
                    sx={{
                      color: "#272827",
                    }}
                  />
                }
                route={RouteType.Teams}
              />

              <HeaderIconButton
                tooltip={"Patients"}
                showLabel={showFullNavButtons}
                buttonRef={setButtonRef(RouteType.Patients)}
                onHoverStart={setHoveredRoute}
                onHoverEnd={() => setHoveredRoute(null)}
                icon={
                  <EmergencyIcon
                    sx={{
                      color: "#272827",
                    }}
                  />
                }
                route={RouteType.Patients}
              />
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" height={"100%"} sx={{ ml: "auto" }}>
            {children}
            <AccountDropdown currentUser={currentUser} />
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderToolbar;
